import React, { useState } from 'react';

const NusmaDemoSection = ({ 
  onNavigateToContact = () => {} 
}) => {
  const [selectedDemo, setSelectedDemo] = useState('audit');
  const [isPlaying, setIsPlaying] = useState(false);

  const demos = {
    audit: {
      title: "NUSMA Audit AI",
      description: "Descubre cómo automatizar tus auditorías ISO 27001 con inteligencia artificial",
      features: [
        "Evaluación automática de 114 controles ISO 27001",
        "Generación de reportes ejecutivos en tiempo real",
        "Análisis predictivo de riesgos de cumplimiento",
        "Dashboard interactivo con métricas clave"
      ],
      benefits: [
        "Reduce tiempo de auditoría en 80%",
        "Elimina errores humanos",
        "Mejora la precisión del cumplimiento",
        "Facilita la toma de decisiones"
      ]
    },
    agent: {
      title: "NUSMA Compliance Agent",
      description: "Experimenta nuestro agente virtual especializado en consultoría de cumplimiento",
      features: [
        "Consultas 24/7 sobre normativas ISO 27001",
        "Recomendaciones personalizadas por industria",
        "Base de conocimiento actualizada constantemente",
        "Integración con sistemas de gestión existentes"
      ],
      benefits: [
        "Acceso inmediato a expertise",
        "Respuestas consistentes y precisas",
        "Reducción de costos de consultoría",
        "Mejora continua del conocimiento"
      ]
    }
  };

  const handlePlayDemo = () => {
    setIsPlaying(true);
    // Simular reproducción de demo
    setTimeout(() => {
      setIsPlaying(false);
    }, 3000);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Experimenta Nuestras Soluciones
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Solicita una demostración personalizada y descubre cómo NUSMA puede 
            transformar tus procesos de auditoría y cumplimiento normativo.
          </p>
        </div>

        {/* Demo Selector */}
        <div className="flex flex-col sm:flex-row justify-center mb-12">
          <div className="bg-gray-900 rounded-2xl p-2 shadow-lg border border-gray-800">
            <button
              onClick={() => setSelectedDemo('audit')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                selectedDemo === 'audit'
                  ? 'bg-gradient-to-r from-red-600 to-red-900 text-white shadow-lg'
                  : 'text-gray-300 hover:text-white hover:bg-gray-800'
              }`}
            >
              NUSMA Audit AI
            </button>
            <button
              onClick={() => setSelectedDemo('agent')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                selectedDemo === 'agent'
                  ? 'bg-gradient-to-r from-red-600 to-red-900 text-white shadow-lg'
                  : 'text-gray-300 hover:text-white hover:bg-gray-800'
              }`}
            >
              NUSMA Compliance Agent
            </button>
          </div>
        </div>

        {/* Demo Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Demo Video/Preview */}
          <div className="order-2 lg:order-1">
            <div className="bg-gray-900 rounded-3xl p-8 shadow-xl border border-gray-800">
              <h3 className="text-2xl font-bold text-white mb-4">
                {demos[selectedDemo].title}
              </h3>
              <p className="text-gray-400 mb-6">
                {demos[selectedDemo].description}
              </p>

              {/* Mock Video Player */}
              <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl overflow-hidden mb-6">
                <div className="aspect-video flex items-center justify-center">
                  {isPlaying ? (
                    <div className="text-white text-center">
                      <div className="animate-spin w-12 h-12 border-4 border-white border-t-transparent rounded-full mx-auto mb-4"></div>
                      <p>Cargando demostración...</p>
                    </div>
                  ) : (
                    <button
                      onClick={handlePlayDemo}
                      className="group flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-all duration-200 transform hover:scale-110"
                    >
                      <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </button>
                  )}
                </div>
                
                {/* Video Overlay Info */}
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="bg-black/50 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-white text-sm font-medium">
                      Demo Interactiva - {demos[selectedDemo].title}
                    </p>
                    <p className="text-gray-300 text-xs">
                      Duración: 5 minutos
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handlePlayDemo}
                  className="flex-1 bg-gradient-to-r from-red-600 to-red-900 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                >
                  Ver Demo Completa
                </button>
                <button
                  onClick={onNavigateToContact}
                  className="flex-1 bg-gray-800 text-gray-200 px-6 py-3 rounded-xl font-semibold border border-gray-700 hover:bg-gray-700 transition-all duration-200"
                >
                  Solicitar Demo Personalizada
                </button>
              </div>
            </div>
          </div>

          {/* Features & Benefits */}
          <div className="order-1 lg:order-2">
            <div className="space-y-8">
              {/* Features */}
              <div className="bg-gray-900 rounded-3xl p-8 shadow-xl border border-gray-800">
                <h4 className="text-xl font-bold text-white mb-6 flex items-center">
                  <div className="w-8 h-8 bg-red-900 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-5 h-5 text-red-300" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  Características Principales
                </h4>
                <div className="space-y-4">
                  {demos[selectedDemo].features.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <div className="w-2 h-2 bg-gradient-to-r from-red-500 to-red-700 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Benefits */}
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-8 border border-gray-700">
                <h4 className="text-xl font-bold text-white mb-6 flex items-center">
                  <div className="w-8 h-8 bg-red-900 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-5 h-5 text-red-300" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M13 10V3L4 14h7v7l9-11h-7z"/>
                    </svg>
                  </div>
                  Beneficios Clave
                </h4>
                <div className="space-y-4">
                  {demos[selectedDemo].benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start">
                      <div className="w-2 h-2 bg-gradient-to-r from-red-500 to-red-700 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-300">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="bg-gray-900 rounded-3xl p-12 shadow-xl border border-gray-800">
            <h3 className="text-2xl font-bold text-white mb-4">
              ¿Listo para ver NUSMA en acción?
            </h3>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Agenda una demostración personalizada con nuestros expertos y descubre 
              cómo podemos adaptar nuestras soluciones a las necesidades específicas de tu organización.
            </p>
            <button
              onClick={onNavigateToContact}
              className="bg-gradient-to-r from-red-600 to-red-900 text-white px-8 py-4 rounded-2xl text-lg font-semibold shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              Agendar Demo Personalizada
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NusmaDemoSection;